/**
 * Core-100 R4 — CORE-BE: tomorrow's capacity, shown BEFORE the carry is decided (see previewCarryCapacity).
 * Reads the ticket's technician (hours, time off, breaks — no `select('*')`), their partial-day absences and
 * their booked work over the next two weeks. Read-only; a failed read is reported, never shown as room.
 * (Plain effect + state rather than react-query: the modal is mounted by surfaces without a query client.)
 */
import { useEffect, useState } from 'react';
import { addDays, format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { ABSENCE_INTERVAL_CAPACITY_COLUMNS, groupAbsenceIntervals, withAbsenceIntervals, type AbsenceIntervalRow } from '@/utils/capacity/absenceIntervals';
import { previewCarryCapacity, type CarryCapacityPreview, type CarryCapacityTicket } from '@/lib/rollover/carryCapacity';

export const CARRY_CAPACITY_TECH_COLUMNS = 'id, name, hours, time_off, recurring_breaks, non_recurring_breaks';

export type CarryCapacityData = { preview: CarryCapacityPreview; techName: string } | null;

export async function loadCarryCapacityPreview(ticketId: string, carryMinutes: number, client: typeof supabase = supabase): Promise<CarryCapacityData> {
  const { data: ticket, error: tErr } = await client
    .from('tickets').select('id, shop_id, assigned_tech_id, scheduled_date').eq('id', ticketId).maybeSingle();
  if (tErr) throw tErr;
  if (!ticket?.assigned_tech_id || !ticket.scheduled_date) return null;
  const [y, m, d] = String(ticket.scheduled_date).split('-').map(Number);
  const until = format(addDays(new Date(y, m - 1, d), 14), 'yyyy-MM-dd');
  const [tech, absences, booked] = await Promise.all([
    client.from('technicians').select(CARRY_CAPACITY_TECH_COLUMNS).eq('id', ticket.assigned_tech_id).maybeSingle(),
    client.from('technician_absence_intervals' as never).select(ABSENCE_INTERVAL_CAPACITY_COLUMNS)
      .eq('technician_id', ticket.assigned_tech_id).gt('absence_date', ticket.scheduled_date).lte('absence_date', until),
    client.from('tickets').select('id, assigned_tech_id, scheduled_date, status, deleted_at, completed_at, opti_time, predicted_minutes')
      .eq('assigned_tech_id', ticket.assigned_tech_id).gt('scheduled_date', ticket.scheduled_date).lte('scheduled_date', until)
      .is('deleted_at', null).limit(500),
  ]);
  for (const r of [tech, absences, booked]) if (r.error) throw r.error;
  if (!tech.data) return null;
  const [withAbsences] = withAbsenceIntervals([tech.data as { id: string }], groupAbsenceIntervals((absences.data ?? []) as unknown as AbsenceIntervalRow[]));
  return {
    techName: String((tech.data as { name?: string }).name ?? 'The technician'),
    preview: previewCarryCapacity({
      tech: withAbsences as unknown as Record<string, unknown> & { id: string },
      tickets: (booked.data ?? []) as CarryCapacityTicket[],
      fromDate: String(ticket.scheduled_date),
      carryMinutes,
      ticketId,
    }),
  };
}

export function useCarryCapacityPreview(ticketId: string, carryMinutes: number, enabled: boolean) {
  const [state, setState] = useState<{ isLoading: boolean; isError: boolean; isSuccess: boolean; data: CarryCapacityData }>(
    { isLoading: false, isError: false, isSuccess: false, data: null });
  useEffect(() => {
    if (!enabled || !ticketId) return;
    let live = true;
    setState({ isLoading: true, isError: false, isSuccess: false, data: null });
    loadCarryCapacityPreview(ticketId, carryMinutes).then(
      (data) => { if (live) setState({ isLoading: false, isError: false, isSuccess: true, data }); },
      () => { if (live) setState({ isLoading: false, isError: true, isSuccess: false, data: null }); },
    );
    return () => { live = false; };
  }, [ticketId, carryMinutes, enabled]);
  return state;
}
